import React, { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PieChart, Pie, Legend
} from "recharts";
import { Download } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};
const COLORS = ["#00ff90","#60a5fa","#c084fc","#fbbf24","#f87171","#34d399","#fb923c"];

function perfColor(score) {
  if (!score) return "rgba(255,255,255,0.2)";
  if (score >= 4.5) return "#00ff90";
  if (score >= 3.5) return "#60a5fa";
  if (score >= 2.5) return "#fbbf24";
  return "#f87171";
}

export default function ProjectAnalytics({ tasks = [], members = [], projects = [] }) {

  /* ── Derived data ── */
  const stats = useMemo(() => {
    const total    = tasks.length;
    const done     = tasks.filter(t => t.status === "Done").length;
    const overdue  = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done").length;
    const onTime   = tasks.filter(t => t.status === "Done" && t.due_date && new Date(t.completed_at || t.updated_at) <= new Date(t.due_date)).length;
    const onTimePct = done ? Math.round((onTime / done) * 100) : 0;
    const completionPct = total ? Math.round((done / total) * 100) : 0;

    // By project
    const byProject = projects.map((p, i) => {
      const pt      = tasks.filter(t => t.project_id === p.id);
      const pdone   = pt.filter(t => t.status === "Done").length;
      const pct     = pt.length ? Math.round((pdone / pt.length) * 100) : 0;
      return { label: p.name.slice(0, 16), value: pct, total: pt.length, color: COLORS[i % COLORS.length] };
    });

    // By member
    const byMember = members.map((m, i) => {
      const mt    = tasks.filter(t => t.assignee_id === m.id);
      const mdone = mt.filter(t => t.status === "Done").length;
      const mOver = mt.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done").length;
      const pct   = mt.length ? Math.round((mdone / mt.length) * 100) : 0;
      return {
        name:       m.name?.split(" ")[0] || m.name,
        fullName:   m.name,
        id:         m.id,
        total:      mt.length,
        done:       mdone,
        overdue:    mOver,
        completion: pct,
        perf:       m.perf_rating,
        color:      COLORS[i % COLORS.length],
      };
    });

    // By priority
    const byPriority = [
      { label: "Critical", value: tasks.filter(t => t.priority === "Critical").length, color: "#f87171" },
      { label: "High",     value: tasks.filter(t => t.priority === "High").length,     color: "#fb923c" },
      { label: "Medium",   value: tasks.filter(t => t.priority === "Medium").length,   color: "#fbbf24" },
      { label: "Low",      value: tasks.filter(t => t.priority === "Low").length,      color: "#60a5fa" },
    ].filter(d => d.value > 0);

    // Status breakdown
    const byStatus = [
      { name: "Backlog",     value: tasks.filter(t => t.status === "Backlog").length     },
      { name: "To Do",       value: tasks.filter(t => t.status === "To Do").length       },
      { name: "In Progress", value: tasks.filter(t => t.status === "In Progress").length },
      { name: "Review",      value: tasks.filter(t => t.status === "Review").length      },
      { name: "Done",        value: tasks.filter(t => t.status === "Done").length        },
    ].filter(d => d.value > 0);

    // Radar: member perf vs task completion
    const radarData = members.slice(0, 8).map(m => {
      const mt  = tasks.filter(t => t.assignee_id === m.id);
      const pct = mt.length ? Math.round((mt.filter(t => t.status === "Done").length / mt.length) * 100) : 0;
      return {
        name:        m.name?.split(" ")[0],
        completion:  pct,
        perfScore:   m.perf_rating ? Math.round(m.perf_rating * 20) : 0, // normalize 0–5 to 0–100
      };
    });

    return { total, done, overdue, onTimePct, completionPct, byProject, byMember, byPriority, byStatus, radarData };
  }, [tasks, members, projects]);

  const exportCsv = () => {
    const rows = stats.byMember.map(m => `${m.fullName},${m.total},${m.done},${m.overdue},${m.completion}%,${m.perf || "—"}`);
    const text = "Member,Total Tasks,Completed,Overdue,Completion %,Perf Score\n" + rows.join("\n");
    const blob = new Blob([text], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "project_analytics.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5">

      {/* Top summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Tasks",    value: stats.total,            color: "#00ff90"  },
          { label: "Completed",      value: stats.done,             color: "#34d399"  },
          { label: "Completion %",   value: `${stats.completionPct}%`, color: "#fbbf24" },
          { label: "On-Time %",      value: `${stats.onTimePct}%`,  color: "#60a5fa"  },
          { label: "Overdue",        value: stats.overdue,          color: stats.overdue > 0 ? "#f87171" : "#ffffff30" },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-4 text-center">
            <p className="text-xl font-bold" style={{ color }}>{value}</p>
            <p className="text-xs text-white/30 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Completion by project */}
        <div className={`${card} p-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Completion by Project</p>
          {stats.byProject.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No projects.</p> : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={stats.byProject} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit="%" domain={[0,100]} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v}%`, "Completion"]} />
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {stats.byProject.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.8} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status breakdown pie */}
        <div className={`${card} p-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Status Breakdown</p>
          {stats.byStatus.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No tasks.</p> : (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={stats.byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} paddingAngle={2}>
                  {stats.byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} fillOpacity={0.85} />)}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Member completion vs perf radar */}
        {stats.radarData.length > 0 && (
          <div className={`${card} p-5`}>
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Task Completion vs Perf Score</p>
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={stats.radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.06)" />
                <PolarAngleAxis dataKey="name" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
                <Radar name="Completion %" dataKey="completion" stroke="#00ff90" fill="#00ff90" fillOpacity={0.12} strokeWidth={2} />
                <Radar name="Perf Score %" dataKey="perfScore"  stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 2" />
                <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
                <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Priority breakdown */}
        <div className={`${card} p-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Tasks by Priority</p>
          {stats.byPriority.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No tasks.</p> : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={stats.byPriority} layout="vertical" margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} width={55} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="value" radius={[0,4,4,0]}>
                  {stats.byPriority.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.8} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Member breakdown table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Member Performance Breakdown</span>
          <button onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
            <Download size={12} /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {["Member","Total","Done","Overdue","Completion","Perf Score","Correlation"].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.byMember.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-white/20 italic text-sm">No data.</td></tr>
              ) : stats.byMember.map(m => {
                // Simple correlation: high completion + high perf = aligned
                const perfNorm = m.perf ? m.perf * 20 : null; // 0–100
                const diff     = perfNorm !== null ? Math.abs(m.completion - perfNorm) : null;
                const corr     = diff === null ? "—" : diff <= 15 ? "✓ Aligned" : diff <= 30 ? "~ Moderate" : "⚠ Misaligned";
                const corrColor = diff === null ? "text-white/25" : diff <= 15 ? "text-[#00ff90]/70" : diff <= 30 ? "text-yellow-400/70" : "text-red-400/70";

                return (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/50">{m.name?.[0]}</div>
                        <span className="text-sm text-white/70">{m.fullName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-white/50 text-xs">{m.total}</td>
                    <td className="px-3 py-3 text-[#00ff90]/70 text-xs">{m.done}</td>
                    <td className="px-3 py-3 text-xs" style={{ color: m.overdue > 0 ? "#f87171" : "rgba(255,255,255,0.3)" }}>{m.overdue}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${m.completion}%` }} />
                        </div>
                        <span className="text-xs text-[#00ff90]/60">{m.completion}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {m.perf
                        ? <span className="text-sm font-bold" style={{ color: perfColor(m.perf) }}>{parseFloat(m.perf).toFixed(1)}</span>
                        : <span className="text-white/20 text-xs">—</span>}
                    </td>
                    <td className={`px-3 py-3 text-xs ${corrColor}`}>{corr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-white/5">
          <p className="text-xs text-white/20">
            "Aligned" means task completion % and perf score are within 15 points of each other.
            Misalignment may indicate that performance ratings aren't reflecting actual output — or vice versa.
          </p>
        </div>
      </div>
    </div>
  );
}